import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Map as MapIcon, CheckCircle } from 'lucide-react';
import Map from './components/Map';
import LayerSelector from './components/LayerSelector';
import DataForm from './components/DataForm';
import GDPRPopup from './components/GDPRPopup';
import LoadingOverlay from './components/LoadingOverlay';
import { sendEmail } from './services/email';
import { generateSerialNumber } from './utils/serialNumber';
import GroupResultsModal from './components/GroupResultsModal';
import GroupResultsPage from './pages/GroupResultsPage';
import type { MapData, FormData } from './types';
import { saveSubmission, getAllStoredSerialNumbers, removeSubmission, recoverSubmissions, getAllSerialNumbers } from './services/submissionService';

// Chave para armazenar submissões recentes no localStorage
const RECENT_SUBMISSIONS_KEY = 'recent_submissions';

// Componente de confirmação modal
const ConfirmationModal = ({ isOpen, onClose, onConfirm }: { isOpen: boolean, onClose: () => void, onConfirm: () => void }) => {
  if (!isOpen) return null;
  
  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 px-4"
    >
      <motion.div 
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        className="bg-white rounded-xl p-6 max-w-md w-full mx-auto shadow-2xl border border-gray-100"
      >
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-3">Confirmação</h2>
          <div className="w-16 h-1 bg-blue-500 mx-auto rounded-full mb-4"></div>
          <p className="text-gray-600 text-lg leading-relaxed">
            Confirmo que preenchi as 3 vistas do protótipo
          </p>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.98 }}
            onClick={onClose}
            className="order-2 sm:order-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-gray-300"
          >
            NÃO (voltar)
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.98 }}
            onClick={onConfirm}
            className="order-1 sm:order-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-blue-400 shadow-md"
          >
            SIM (submeter)
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Novo componente para o agradecimento
const ThankYouModal = ({ 
  isOpen, 
  onClose, 
  serialNumber 
}: { 
  isOpen: boolean, 
  onClose: () => void, 
  serialNumber: string 
}) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div 
            className="bg-white rounded-lg shadow-lg w-[320px] p-3 flex flex-col"
            style={{ height: "auto" }}
          >
            <div className="text-center">
              <CheckCircle className="w-10 h-10 text-green-500 mx-auto" />
              <h2 className="text-base font-bold mt-1">
                Obrigada pela sua participação!
              </h2>
            </div>
            
            <div className="mt-2 text-xs text-center">
              <p>
                Para esclarecimentos contactar:
              </p>
              <p className="font-medium text-blue-700">
                rita.martelo@edu.ulisboa.pt
              </p>
            </div>
            
            <div className="mt-2 bg-gray-50 border border-gray-200 rounded p-1 text-center">
              <p className="text-xs text-gray-600">Número de série:</p>
              <p className="text-xs font-mono font-semibold text-blue-700">{serialNumber}</p>
            </div>
            
            <p className="mt-2 text-xs text-center">
              Submissão disponível em "Ver Mapas do Grupo"
            </p>
            
            <button
              onClick={onClose}
              className="mt-3 px-4 py-1 bg-blue-600 text-white text-xs rounded mx-auto hover:bg-blue-700"
            >
              Entendido
            </button>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
};

const initialMapsData: MapData[] = [
  {
    id: 'city',
    baseImageUrl: '/maps/city/base.png',
    baseImageUrlGray: '/maps/city/base-gray.png',
    baseImageUrlSimple: '/maps/city/base-simple.png',
    layers: [
      {
        id: 'bike-lanes',
        name: 'Pista Ciclável',
        imageUrl: '/maps/city/layers/bike-lanes.png',
        isActive: false,
        section: 'Infraestrutura'
      },
      {
        id: 'bike-paths',
        name: 'Faixa Ciclável',
        imageUrl: '/maps/city/layers/bike-paths.png',
        isActive: false,
        section: 'Infraestrutura'
      },
      {
        id: 'shared-paths',
        name: 'Via Partilhada',
        imageUrl: '/maps/city/layers/shared-paths.png',
        isActive: false,
        section: 'Infraestrutura'
      },
      {
        id: 'bike-repair',
        name: 'Manutenção/Reparação',
        imageUrl: '/maps/city/layers/bike-repair.png',
        isActive: false,
        section: 'Outros'
      },
      {
        id: 'danger-zones',
        name: 'Perigos',
        imageUrl: '/maps/city/layers/danger-zones.png',
        isActive: false,
        section: 'Elementos de Suporte'
      },
      {
        id: 'gira-stations',
        name: 'Estação GIRA',
        imageUrl: '/maps/city/layers/gira-stations.png',
        isActive: false,
        section: 'Elementos de Suporte'
      },
      {
        id: 'bike-parking',
        name: 'Estacionamento bicicletas',
        imageUrl: '/maps/city/layers/bike-parking.png',
        isActive: false,
        section: 'Elementos de Suporte'
      },
      {
        id: 'points-of-interest',
        name: 'Pontos de interesse',
        imageUrl: '/maps/city/layers/points-of-interest.png',
        isActive: false,
        section: 'Outros'
      },
      {
        id: 'green-zones',
        name: 'Zonas Verdes',
        imageUrl: '/maps/city/layers/green-zones.png',
        isActive: false,
        section: 'Zonas a Destacar'
      },
      {
        id: '30-zones',
        name: 'Zona 30 + Bicicletas',
        imageUrl: '/maps/city/layers/30-zones.png',
        isActive: false,
        section: 'Zonas a Destacar'
      },
      {
        id: 'volume-transito',
        name: 'Volume de trânsito (zonas congestionadas)',
        imageUrl: '/maps/city/layers/volume-transito.png',
        isActive: false,
        section: 'Zonas a Destacar'
      },
      {
        id: 'legenda-texto',
        name: 'Legenda Texto',
        imageUrl: '/maps/city/layers/legenda-texto.png',
        isActive: false,
        section: 'Texto'
      },
      {
        id: 'train',
        name: 'Comboio',
        imageUrl: '/maps/city/layers/train.png',
        isActive: false,
        section: 'Transportes Públicos'
      },
      {
        id: 'metro',
        name: 'Metro',
        imageUrl: '/maps/city/layers/metro.png',
        isActive: false,
        section: 'Transportes Públicos'
      },
      {
        id: 'traffic',
        name: 'Volume de trânsito (zonas congestionadas)',
        imageUrl: '/maps/city/layers/traffic.png',
        isActive: false,
        section: 'Zonas a Destacar'
      },
      {
        id: 'zone30',
        name: 'Zona 30 + Bicicletas',
        imageUrl: '/maps/city/layers/zone30.png',
        isActive: false,
        section: 'Zonas a Destacar'
      },
      {
        id: 'direction',
        name: 'Sentido da marcha',
        imageUrl: '/maps/city/layers/direction.png',
        isActive: false,
        section: 'Infraestrutura'
      }
    ]
  },
  {
    id: 'area',
    baseImageUrl: '/maps/area/base.png',
    baseImageUrlGray: '/maps/area/base-gray.png',
    baseImageUrlSimple: '/maps/area/base-simple.png',
    layers: [
      {
        id: 'bike-lanes',
        name: 'Pista Ciclável',
        imageUrl: '/maps/area/layers/bike-lanes.png',
        isActive: false,
        section: 'Infraestrutura'
      },
      {
        id: 'bike-paths',
        name: 'Faixa Ciclável',
        imageUrl: '/maps/area/layers/bike-paths.png',
        isActive: false,
        section: 'Infraestrutura'
      },
      {
        id: 'shared-paths',
        name: 'Via Partilhada',
        imageUrl: '/maps/area/layers/shared-paths.png',
        isActive: false,
        section: 'Infraestrutura'
      },
      {
        id: 'bike-repair',
        name: 'Manutenção/Reparação',
        imageUrl: '/maps/area/layers/bike-repair.png',
        isActive: false,
        section: 'Outros'
      },
      {
        id: 'danger-zones',
        name: 'Perigos',
        imageUrl: '/maps/area/layers/danger-zones.png',
        isActive: false,
        section: 'Outros'
      },
      {
        id: 'gira-stations',
        name: 'Estação GIRA',
        imageUrl: '/maps/area/layers/gira-stations.png',
        isActive: false,
        section: 'Outros'
      },
      {
        id: 'bike-parking',
        name: 'Estacionamento Bicicleta',
        imageUrl: '/maps/area/layers/bike-parking.png',
        isActive: false,
        section: 'Elementos de Suporte'
      },
      {
        id: 'points-of-interest',
        name: 'Pontos de interesse',
        imageUrl: '/maps/area/layers/points-of-interest.png',
        isActive: false,
        section: 'Outros'
      },
      {
        id: 'green-zones',
        name: 'Zonas Verdes',
        imageUrl: '/maps/area/layers/green-zones.png',
        isActive: false,
        section: 'Zonas a Destacar'
      },
      {
        id: '30-zones',
        name: 'Zona 30 + Bicicletas',
        imageUrl: '/maps/area/layers/30-zones.png',
        isActive: false,
        section: 'Zonas a Destacar'
      },
      {
        id: 'volume-transito',
        name: 'Volume de trânsito (zonas congestionadas)',
        imageUrl: '/maps/area/layers/volume-transito.png',
        isActive: false,
        section: 'Zonas a Destacar'
      },
      {
        id: 'legenda-texto',
        name: 'Legenda Texto',
        imageUrl: '/maps/area/layers/legenda-texto.png',
        isActive: false,
        section: 'Texto'
      },
      {
        id: 'train',
        name: 'Comboio',
        imageUrl: '/maps/area/layers/train.png',
        isActive: false,
        section: 'Transportes Públicos'
      },
      {
        id: 'metro',
        name: 'Metro',
        imageUrl: '/maps/area/layers/metro.png',
        isActive: false,
        section: 'Transportes Públicos'
      },
      {
        id: 'traffic',
        name: 'Volume de trânsito (zonas congestionadas)',
        imageUrl: '/maps/area/layers/traffic.png',
        isActive: false,
        section: 'Zonas a Destacar'
      },
      {
        id: 'zone30',
        name: 'Zona 30 + Bicicletas',
        imageUrl: '/maps/area/layers/zone30.png',
        isActive: false,
        section: 'Infraestrutura'
      },
      {
        id: 'direction',
        name: 'Sentido da marcha',
        imageUrl: '/maps/area/layers/direction.png',
        isActive: false,
        section: 'Infraestrutura'
      }
    ]
  },
  {
    id: 'street',
    baseImageUrl: '/maps/street/base.png',
    baseImageUrlGray: '/maps/street/base-gray.png',
    baseImageUrlSimple: '/maps/street/base-simple.png',
    layers: [
      {
        id: 'bike-lanes',
        name: 'Pista Ciclável',
        imageUrl: '/maps/street/layers/bike-lanes.png',
        isActive: false,
        section: 'Infraestrutura'
      },
      {
        id: 'bike-paths',
        name: 'Faixa Ciclável',
        imageUrl: '/maps/street/layers/bike-paths.png',
        isActive: false,
        section: 'Infraestrutura'
      },
      {
        id: 'shared-paths',
        name: 'Via Partilhada',
        imageUrl: '/maps/street/layers/shared-paths.png',
        isActive: false,
        section: 'Infraestrutura'
      },
      {
        id: 'bike-repair',
        name: 'Manutenção/Reparação',
        imageUrl: '/maps/street/layers/bike-repair.png',
        isActive: false,
        section: 'Outros'
      },
      {
        id: 'danger-zones',
        name: 'Perigos',
        imageUrl: '/maps/street/layers/danger-zones.png',
        isActive: false,
        section: 'Outros'
      },
      {
        id: 'gira-stations',
        name: 'Estação GIRA',
        imageUrl: '/maps/street/layers/gira-stations.png',
        isActive: false,
        section: 'Outros'
      },
      {
        id: 'bike-parking',
        name: 'Estacionamento Bicicletas',
        imageUrl: '/maps/street/layers/bike-parking.png',
        isActive: false,
        section: 'Outros'
      },
      {
        id: 'points-of-interest',
        name: 'Pontos de interesse',
        imageUrl: '/maps/street/layers/points-of-interest.png',
        isActive: false,
        section: 'Outros'
      },
      {
        id: 'green-zones',
        name: 'Zonas Verdes',
        imageUrl: '/maps/street/layers/green-zones.png',
        isActive: false,
        section: 'Zonas a Destacar'
      },
      {
        id: '30-zones',
        name: 'Zona 30 Bicicletas',
        imageUrl: '/maps/street/layers/30-zones.png',
        isActive: false,
        section: 'Outros'
      },
      {
        id: 'volume-transito',
        name: 'Volume de Transito',
        imageUrl: '/maps/street/layers/volume-transito.png',
        isActive: false,
        section: 'Zonas a Destacar'
      },
      {
        id: 'legenda-texto',
        name: 'Legenda Texto',
        imageUrl: '/maps/street/layers/legenda-texto.png',
        isActive: false,
        section: 'Texto'
      },
      {
        id: 'train',
        name: 'Comboio',
        imageUrl: '/maps/street/layers/train.png',
        isActive: false,
        section: 'Transportes Públicos'
      },
      {
        id: 'metro',
        name: 'Metro',
        imageUrl: '/maps/street/layers/metro.png',
        isActive: false,
        section: 'Transportes Públicos'
      },
      {
        id: 'traffic',
        name: 'Volume de trânsito (zonas congestionadas)',
        imageUrl: '/maps/street/layers/traffic.png',
        isActive: false,
        section: 'Zonas a Destacar'
      },
      {
        id: 'zone30',
        name: 'Zona 30 + Bicicletas',
        imageUrl: '/maps/street/layers/zone30.png',
        isActive: false,
        section: 'Infraestrutura'
      },
      {
        id: 'direction',
        name: 'Sentido da marcha',
        imageUrl: '/maps/street/layers/direction.png',
        isActive: false,
        section: 'Infraestrutura'
      }
    ]
  }
];

// Definição de páginas para roteamento
type AppPage = 'main' | 'group-results';

function App() {
  const [mapsData, setMapsData] = useState<MapData[]>(initialMapsData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submissions, setSubmissions] = useState<FormData[]>([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [currentMapIndex, setCurrentMapIndex] = useState(0);
  const [mapStyles, setMapStyles] = useState<Record<string, 'color' | 'gray' | 'simple'>>({
    city: 'color',
    area: 'color',
    street: 'color'
  });
  const [submissionStage, setSubmissionStage] = useState<'city' | 'area' | 'street' | 'sending' | 'complete' | null>(null);
  const [submitButtonEnabled, setSubmitButtonEnabled] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isGroupResultsModalOpen, setIsGroupResultsModalOpen] = useState(false);
  const [recentSubmissions, setRecentSubmissions] = useState<string[]>(() => {
    // Carregar submissões recentes do localStorage ao iniciar
    try {
      const saved = localStorage.getItem(RECENT_SUBMISSIONS_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      console.error('Erro ao carregar submissões recentes:', error);
    }
    return [];
  });
  const [showSubmissionNotification, setShowSubmissionNotification] = useState(false);
  const [isThankYouModalOpen, setIsThankYouModalOpen] = useState(false);
  const [lastSerialNumber, setLastSerialNumber] = useState('');
  
  // Estado para controle de roteamento
  const [currentPage, setCurrentPage] = useState<AppPage>('main');
  
  // Estado para controlar a exibição do GDPR - começa como true apenas na primeira visita
  const [showGDPRPopup, setShowGDPRPopup] = useState<boolean>(() => {
    // Verificar se o usuário já aceitou o GDPR anteriormente
    const gdprAccepted = localStorage.getItem('gdpr_accepted');
    return !gdprAccepted;
  });

  const cityMapRef = useRef<HTMLDivElement | null>(null);
  const areaMapRef = useRef<HTMLDivElement | null>(null);
  const streetMapRef = useRef<HTMLDivElement | null>(null);

  // Efeito para salvar submissões recentes no localStorage sempre que elas mudarem
  useEffect(() => {
    try {
      localStorage.setItem(RECENT_SUBMISSIONS_KEY, JSON.stringify(recentSubmissions));
    } catch (error) {
      console.error('Erro ao salvar submissões recentes:', error);
    }
  }, [recentSubmissions]);
  
  // Efeito para carregar todas as submissões salvas ao iniciar o app
  useEffect(() => {
    // Carrega todas as submissões salvas no armazenamento
    const allStoredSubmissions = getAllStoredSerialNumbers();
    console.log("Submissões armazenadas encontradas:", allStoredSubmissions);
    
    // Se houver submissões salvas, mas nenhuma na lista recente, adiciona-as
    if (allStoredSubmissions.length > 0) {
      if (recentSubmissions.length === 0) {
        // Limita a 5 itens mais recentes
        const recentOnes = allStoredSubmissions.slice(0, 5);
        console.log("Recuperando submissões:", recentOnes);
        setRecentSubmissions(recentOnes);
      } else {
        // Garante que todas as submissões recentes existem no localStorage
        const validSubmissions = recentSubmissions.filter(sn => 
          allStoredSubmissions.includes(sn)
        );
        
        if (validSubmissions.length !== recentSubmissions.length) {
          console.log("Ajustando lista de submissões recentes:", validSubmissions);
          setRecentSubmissions(validSubmissions);
        }
      }
    }
  }, []);

  const handleMapStyleChange = (mapId: string, style: 'color' | 'gray' | 'simple') => {
    setMapStyles(prev => ({ ...prev, [mapId]: style }));
    setMapsData(maps =>
      maps.map(map => {
        if (map.id === mapId) {
          const originalMap = initialMapsData.find(m => m.id === mapId);
          if (!originalMap) return map;

          let newBaseImageUrl;
          switch (style) {
            case 'color':
              newBaseImageUrl = originalMap.baseImageUrl;
              break;
            case 'gray':
              newBaseImageUrl = originalMap.baseImageUrlGray;
              break;
            case 'simple':
              newBaseImageUrl = originalMap.baseImageUrlSimple;
              break;
            default:
              newBaseImageUrl = originalMap.baseImageUrl;
          }
          
          // Se for o mapa de rua e o estilo não for simplificado, desativa a camada de legenda de texto
          if (mapId === 'street' && style !== 'simple') {
            return {
              ...map,
              baseImageUrl: newBaseImageUrl,
              layers: map.layers.map(layer => 
                layer.id === 'legenda-texto' ? { ...layer, isActive: false } : layer
              )
            };
          }
          
          return {
            ...map,
            baseImageUrl: newBaseImageUrl
          };
        }
        return map;
      })
    );
  };

  const handleToggleLayer = (mapId: string, layerId: string) => {
    setMapsData(maps =>
      maps.map(map => {
        // Only update the specific map's layers
        if (map.id === mapId) {
          return {
            ...map,
            layers: map.layers.map(layer => {
              if (layer.id === layerId) {
                // Toggle the layer's active state
                return { ...layer, isActive: !layer.isActive };
              }
              return layer;
            })
          };
        }
        // Keep other maps' layers unchanged
        return map;
      })
    );
  };

  const handleNextStep = () => {
    setCurrentStep(prev => Math.min(prev + 1, 2));
  };

  const handlePreviousStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  // Effect para controlar quando o botão submit deve estar disponível
  useEffect(() => {
    if (currentStep === 2) {
      // Desabilitar o botão e definir um timeout para habilitá-lo após 3 segundos
      setSubmitButtonEnabled(false);
      const timer = setTimeout(() => {
        setSubmitButtonEnabled(true);
      }, 3000); // 3 segundos
      
      return () => clearTimeout(timer);
    } else {
      setSubmitButtonEnabled(false);
    }
  }, [currentStep]);

  // Esta função agora apenas abre o modal de confirmação
  const handleSubmit = () => {
    setIsConfirmModalOpen(true);
  };

  // Esta função realiza a submissão real após a confirmação
  const submitForm = async () => {
    try {
      setIsSubmitting(true);
      setSubmitError(null);
      setSubmissionStage('city');
      
      const serialNumber = generateSerialNumber();
      console.log('Serial number:', serialNumber);
      
      // Inicialmente, esconder todos os mapas
      if (cityMapRef.current) cityMapRef.current.style.display = 'none';
      if (areaMapRef.current) areaMapRef.current.style.display = 'none';
      if (streetMapRef.current) streetMapRef.current.style.display = 'none';

      // Capturar os mapas sequencialmente para evitar problemas de interferência
      console.log('Iniciando processo sequencial de captura de mapas');
      
      // Criar um objeto simplificado com apenas as camadas selecionadas
      const selectedLayersData = {
        selectedLayers: getSelectedLayers()
      };
      
      // 1. Capturar o mapa da cidade
      if (cityMapRef.current) {
        console.log('Referência do mapa da cidade encontrada:', cityMapRef.current.id);
        
        const cleanupCityMap = prepareMapForCapture(cityMapRef);
        console.log('Mapa da cidade preparado para captura');
        
        try {
          console.log('Enviando solicitação para capturar o mapa da cidade');
          const cityCapture = await sendEmail(
            { 
              ...selectedLayersData, 
              captureMode: 'city'
            }, 
            serialNumber,
            { cityMap: cityMapRef.current, areaMap: null, streetMap: null },
            mapsData,
            mapStyles
          );
          console.log('Resposta da captura do mapa da cidade:', cityCapture);
        } catch (error) {
          console.error('Erro ao capturar o mapa da cidade:', error);
        } finally {
          // Restaurar o estado original do mapa da cidade
          cleanupCityMap();
        }
      } else {
        console.error('Referência do mapa da cidade não encontrada');
      }
      
      // Aguardar um pouco antes de preparar o próximo mapa
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // 2. Capturar o mapa da área
      setSubmissionStage('area');
      if (areaMapRef.current) {
        console.log('Referência do mapa da área encontrada:', areaMapRef.current.id);
        
        const cleanupAreaMap = prepareMapForCapture(areaMapRef);
        console.log('Mapa da área preparado para captura');
        
        try {
          console.log('Enviando solicitação para capturar o mapa da área');
          const areaCapture = await sendEmail(
            { 
              ...selectedLayersData, 
              captureMode: 'area'
            }, 
            serialNumber,
            { cityMap: null, areaMap: areaMapRef.current, streetMap: null },
            mapsData,
            mapStyles
          );
          console.log('Resposta da captura do mapa da área:', areaCapture);
        } catch (error) {
          console.error('Erro ao capturar o mapa da área:', error);
        } finally {
          // Restaurar o estado original do mapa da área
          cleanupAreaMap();
        }
      } else {
        console.error('Referência do mapa da área não encontrada');
      }
      
      // Aguardar um pouco antes de preparar o próximo mapa
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // 3. Capturar o mapa da rua e enviar o email
      setSubmissionStage('street');
      if (streetMapRef.current) {
        console.log('Referência do mapa da rua encontrada:', streetMapRef.current.id);
        
        const cleanupStreetMap = prepareMapForCapture(streetMapRef);
        console.log('Mapa da rua preparado para captura');
        
        try {
          console.log('Enviando solicitação para capturar o mapa da rua e enviar o email');
          setSubmissionStage('sending');
          const response = await sendEmail(
            { 
              ...selectedLayersData, 
              captureMode: 'street_and_send'
            }, 
            serialNumber,
            { cityMap: null, areaMap: null, streetMap: streetMapRef.current },
            mapsData,
            mapStyles
          );
          
          console.log('Resposta do envio de email:', response);
          setSubmissionStage('complete');
          
          // Salvar a submissão com as camadas e estilos selecionados atuais
          saveSubmission(serialNumber, mapsData, mapStyles);
          
          // Adicionar o novo número de série à lista de submissões recentes
          setRecentSubmissions(prev => {
            // Adiciona o novo número no início e mantém apenas os 5 mais recentes
            const newList = [serialNumber, ...prev];
            if (newList.length > 5) {
              newList.length = 5;
            }
            return newList;
          });
          
          // Mostrar notificação sobre a disponibilidade da submissão
          setShowSubmissionNotification(true);
          
          // Ocultar a notificação após 10 segundos
          setTimeout(() => {
            setShowSubmissionNotification(false);
          }, 10000);
          
          // Aguardar um momento para que o usuário veja a mensagem de sucesso
          await new Promise(resolve => setTimeout(resolve, 2000));
          
          // Substituir o alert pelo modal customizado
          setLastSerialNumber(serialNumber);
          setIsThankYouModalOpen(true);
          
          setTimeout(() => {
            setMapsData(initialMapsData);
            setCurrentStep(0);
            setSubmissionStage(null);
          }, 1000);
        } catch (error) {
          console.error('Erro ao enviar o formulário:', error);
          if (error instanceof Error) {
            setSubmitError(error.message);
          } else {
            setSubmitError('Ocorreu um erro ao enviar o formulário. Por favor, tente novamente.');
          }
          setSubmissionStage(null);
        } finally {
          cleanupStreetMap();
        }
      } else {
        console.error('Referência do mapa da rua não encontrada');
        setSubmissionStage(null);
      }
      
    } catch (error) {
      console.error('Erro ao enviar o formulário:', error);
      if (error instanceof Error) {
        setSubmitError(error.message);
      } else {
        setSubmitError('Ocorreu um erro ao enviar o formulário. Por favor, tente novamente.');
      }
      setSubmissionStage(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getSelectedLayers = () => ({
    cityMap: mapsData[0].layers.filter(l => l.isActive).map(l => l.id),
    areaMap: mapsData[1].layers.filter(l => l.isActive).map(l => l.id),
    streetMap: mapsData[2].layers.filter(l => l.isActive).map(l => l.id),
  });

  const getSelectedLayersForMap = (mapId: string) => {
    const map = mapsData.find(m => m.id === mapId);
    if (map) {
      return map.layers.filter(l => l.isActive).map(l => l.id);
    }
    return [];
  };

  const prepareMapForCapture = (mapRef: React.RefObject<HTMLDivElement>) => {
    if (mapRef.current) {
      console.log('Preparando mapa para captura:', mapRef.current.id);
      
      // Garantir que o mapa esteja visível e com dimensões adequadas
      const originalStyle = {
        display: mapRef.current.style.display,
        position: mapRef.current.style.position,
        width: mapRef.current.style.width,
        height: mapRef.current.style.height,
        zIndex: mapRef.current.style.zIndex,
        opacity: mapRef.current.style.opacity,
        visibility: mapRef.current.style.visibility
      };
      
      // Forçar o mapa a ser visível e com dimensões fixas
      // Mas fora da área visível para o usuário usando position: fixed e left: -9999px
      mapRef.current.style.display = 'block';
      mapRef.current.style.position = 'fixed';
      mapRef.current.style.left = '-9999px';
      mapRef.current.style.width = '1200px';
      mapRef.current.style.height = '900px';
      mapRef.current.style.zIndex = '999'; // Alto valor para estar no topo, mas fora da tela
      mapRef.current.style.opacity = '1';
      mapRef.current.style.visibility = 'visible';
      
      // Forçar um repaint do elemento
      mapRef.current.getBoundingClientRect();
      console.log('Mapa preparado. Novas dimensões:', {
        width: mapRef.current.offsetWidth,
        height: mapRef.current.offsetHeight
      });
      
      // Retornar função para restaurar o estado original
      return () => {
        if (mapRef.current) {
          mapRef.current.style.display = originalStyle.display;
          mapRef.current.style.position = originalStyle.position;
          mapRef.current.style.width = originalStyle.width;
          mapRef.current.style.height = originalStyle.height;
          mapRef.current.style.zIndex = originalStyle.zIndex;
          mapRef.current.style.opacity = originalStyle.opacity;
          mapRef.current.style.visibility = originalStyle.visibility;
          if (mapRef.current.style.left === '-9999px') {
            mapRef.current.style.left = '';
          }
          console.log('Estado original do mapa restaurado');
        }
      };
    }
    return () => {};
  };

  // Manipulador para alternar entre páginas
  const handleViewGroupResults = () => {
    setCurrentPage('group-results');
  };
  
  const handleBackToMain = () => {
    // Sempre define showGDPRPopup como false ao voltar da página de resultados
    setShowGDPRPopup(false);
    
    // Apenas muda para a página principal
    setCurrentPage('main');
    
    // Garantir que nenhum modal esteja aberto ao voltar para a página principal
    setIsConfirmModalOpen(false);
    setIsGroupResultsModalOpen(false);
    setIsThankYouModalOpen(false);
  };

  // Função para salvar a aceitação do GDPR
  const handleGDPRAccept = () => {
    setShowGDPRPopup(false);
    localStorage.setItem('gdpr_accepted', 'true');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {currentPage === 'main' ? (
        // Página principal - protótipo
        <>
          <LoadingOverlay isOpen={!!submissionStage} submissionStage={submissionStage || 'city'} />
          <GDPRPopup isOpen={showGDPRPopup} onAccept={handleGDPRAccept} />
          <ConfirmationModal 
            isOpen={isConfirmModalOpen}
            onClose={() => setIsConfirmModalOpen(false)}
            onConfirm={() => {
              setIsConfirmModalOpen(false);
              submitForm();
            }}
          />
          <ThankYouModal
            isOpen={isThankYouModalOpen}
            onClose={() => setIsThankYouModalOpen(false)}
            serialNumber={lastSerialNumber}
          />
          <GroupResultsModal
            isOpen={isGroupResultsModalOpen}
            onClose={() => setIsGroupResultsModalOpen(false)}
            mapsData={mapsData}
            mapStyles={mapStyles}
            recentSubmissions={recentSubmissions}
            onRemoveSubmission={(serialNumber) => {
              // Remove da lista de submissões recentes
              setRecentSubmissions(prev => prev.filter(sn => sn !== serialNumber));
              // Remove também do armazenamento persistente
              removeSubmission(serialNumber);
            }}
            onRecoverSubmissions={(serialNumbers) => {
              setRecentSubmissions(prev => {
                // Adiciona os números recuperados, mas evita duplicatas e mantém o limite de 5
                const uniqueList = [...new Set([...prev, ...serialNumbers])];
                if (uniqueList.length > 5) {
                  return uniqueList.slice(0, 5);
                }
                return uniqueList;
              });
            }}
          />
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-center mb-2 sm:mb-4"
            >
              <h1 className="mt-1 sm:mt-3 text-xl sm:text-3xl font-bold text-gray-900">
                Workshop - Mapa Participativo
              </h1>
              <p className="text-sm sm:text-base text-gray-600 mt-1">
                (contributo de grupo)
              </p>
            </motion.div>

            <div className="space-y-3">
              {/* Botão destacado para ver resultados de grupo */}
              <div className="flex justify-center mb-4">
                <button
                  onClick={handleViewGroupResults}
                  className="px-4 py-2 bg-gradient-to-r from-blue-600 to-green-500 text-white rounded-md shadow-lg hover:shadow-xl transform transition-all hover:scale-105 flex items-center justify-center gap-2 relative"
                >
                  <MapIcon className="w-5 h-5" />
                  <span>Ver Mapas de Grupos</span>
                  
                  {/* Notificação de nova submissão */}
                  {showSubmissionNotification && (
                    <span className="absolute -top-2 -right-2 flex h-5 w-5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-5 w-5 bg-green-500 text-xs items-center justify-center">
                        ✓
                      </span>
                    </span>
                  )}
                </button>
              </div>
              
              <div className="bg-white p-2 sm:p-3 rounded-xl shadow-lg">
                <div className="flex flex-wrap justify-between items-center mb-4">
                  <div className="flex flex-wrap gap-1 sm:gap-2 mb-2 sm:mb-0 w-full sm:w-auto">
                    <button
                      className={`px-2 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm rounded-md transition-colors ${currentStep === 0 ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                      onClick={() => setCurrentStep(0)}
                    >
                      Vista 1 - Cidade
                    </button>
                    <button
                      className={`px-2 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm rounded-md transition-colors ${currentStep === 1 ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                      onClick={() => setCurrentStep(1)}
                    >
                      Vista 2 - Bairro
                    </button>
                    <button
                      className={`px-2 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm rounded-md transition-colors ${currentStep === 2 ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                      onClick={() => setCurrentStep(2)}
                    >
                      Vista 3 - Rua
                    </button>
                  </div>
                </div>

                {/* Layout responsivo para mapas e seletor de camadas */}
                <div className="flex flex-row gap-2 overflow-hidden">
                  <div className="w-3/5 md:w-3/4 h-full flex-shrink-0" style={{ minHeight: '280px', minWidth: '240px' }}>
                    <Map
                      data={mapsData[currentStep]}
                      onMapRef={ref => {
                        if (currentStep === 0) cityMapRef.current = ref;
                        else if (currentStep === 1) areaMapRef.current = ref;
                        else if (currentStep === 2) streetMapRef.current = ref;
                      }}
                    />
                  </div>
                  <div className="w-2/5 md:w-1/4 h-full flex-shrink-0" style={{ minHeight: '280px', minWidth: '120px' }}>
                    <LayerSelector
                      mapData={mapsData[currentStep]}
                      onToggleLayer={(layerId) => handleToggleLayer(mapsData[currentStep].id, layerId)}
                      onStyleChange={(style) => handleMapStyleChange(mapsData[currentStep].id, style)}
                      currentStyle={mapStyles[mapsData[currentStep].id]}
                    />
                  </div>
                </div>

                {/* Mapas ocultos para captura mantidos os mesmos */}
                <div style={{ display: 'none', position: 'absolute', left: '-9999px' }}>
                  <Map
                    data={mapsData[0]}  // Dados do mapa cidade
                    onMapRef={ref => { 
                      cityMapRef.current = ref;
                    }}
                  />
                </div>

                <div style={{ display: 'none', position: 'absolute', left: '-9999px' }}>
                  <Map
                    data={mapsData[1]}  // Dados do mapa área
                    onMapRef={ref => { 
                      areaMapRef.current = ref;
                    }}
                  />
                </div>

                <div style={{ display: 'none', position: 'absolute', left: '-9999px' }}>
                  <Map
                    data={mapsData[2]}  // Dados do mapa rua
                    onMapRef={ref => { 
                      streetMapRef.current = ref;
                    }}
                  />
                </div>
                
                {submitError && (
                  <div className="mt-4 text-red-600 text-center text-sm">
                    {submitError}
                  </div>
                )}

                <div className="flex justify-between mt-3 sm:mt-4">
                  <button
                    onClick={handlePreviousStep}
                    disabled={currentStep === 0}
                    className="px-3 py-1.5 text-xs sm:text-sm sm:px-4 sm:py-2 rounded-md bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Anterior
                  </button>
                  
                  {currentStep < 2 ? (
                    <button
                      onClick={handleNextStep}
                      className="px-3 py-1.5 text-xs sm:text-sm sm:px-4 sm:py-2 rounded-md bg-blue-500 text-white hover:bg-blue-600"
                    >
                      Próximo
                    </button>
                  ) : (
                    <button
                      onClick={handleSubmit}
                      disabled={isSubmitting || !submitButtonEnabled}
                      className={`px-3 py-1.5 text-xs sm:text-sm sm:px-4 sm:py-2 rounded-md ${submitButtonEnabled ? 'bg-green-600 text-white hover:bg-green-700 shadow-md transform hover:scale-105 transition-all' : 'bg-gray-400 text-white'} disabled:opacity-50 disabled:cursor-not-allowed ${!submitButtonEnabled ? 'relative overflow-hidden' : ''}`}
                    >
                      {!submitButtonEnabled && (
                        <div className="absolute inset-0 flex items-center justify-center bg-gray-400 bg-opacity-80">
                          <span className="text-xs sm:text-sm text-white">Aguarde...</span>
                        </div>
                      )}
                      Submeter
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        // Página de resultados do grupo
        <GroupResultsPage
          mapsData={mapsData}
          mapStyles={mapStyles}
          recentSubmissions={recentSubmissions}
          onRemoveSubmission={(serialNumber) => {
            // Remove da lista de submissões recentes
            setRecentSubmissions(prev => prev.filter(sn => sn !== serialNumber));
            // Remove também do armazenamento persistente
            removeSubmission(serialNumber);
          }}
          onRecoverSubmissions={(serialNumbers) => {
            setRecentSubmissions(prev => {
              // Adiciona os números recuperados, mas evita duplicatas e mantém o limite de 5
              const uniqueList = [...new Set([...prev, ...serialNumbers])];
              if (uniqueList.length > 5) {
                return uniqueList.slice(0, 5);
              }
              return uniqueList;
            });
          }}
          onBack={handleBackToMain}
        />
      )}
    </div>
  );
}

export default App;